import { AnimatePresence, motion } from 'framer-motion';
import styles from "./FilterResults.module.css";
import React, { FC, useState } from 'react';
import { Button } from 'components/atoms';
import { useDeviceClassification } from 'utils';
import { useFilterContext } from 'components/templates/showcase/content';
import ClipLoader from "react-spinners/ClipLoader";
import { Error } from "./components/Error";
import { LargeCard, RelatedContentCard } from 'components/molecules';


export type TResult = {
    Name: string,
    Url: string,
    Abstract: string,
    Img: any
}

type TFilterResults = {
    initialSubpages: TResult[];
    layoutConfig: '3-Col' | 'Large-Card'
    _ShowPublicationDateReadTimeContentType: boolean;
    showFilters: boolean;
};

const variant = {
    hidden: {
        scale: 0,
        opacity: 0,
        y: 200
    },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0
    },
    exit: {
        scale: 0,
        opacity: 0,
        y: 200
    }
}

const _FilterResults: FC<TFilterResults> = ({
    initialSubpages,
    layoutConfig,
    _ShowPublicationDateReadTimeContentType,
    showFilters
}) => {

    const {
        filterResults,
        handleLoadResults,
        similarResultsDisplayed,
        showSimilarResults,
        error,
        loadingMore,
        loadingResults,
        filterResultsReset,
        loadMoreError
    } = useFilterContext();
    const results = filterResultsReset ? initialSubpages : filterResults || initialSubpages;
    const { isDesktop } = useDeviceClassification();

    const threeColLayout = layoutConfig === '3-Col';
    const tileWidth = threeColLayout ? `${Math.floor(document.querySelector('#page').getBoundingClientRect().width / 3.5)}px` : '100%';

    return (
        <>
            <AnimatePresence exitBeforeEnter>
                {loadingResults ?
                    <motion.div
                        style={{ display: 'flex', justifyContent: 'center', margin: '0 auto' }}
                        variants={variant}
                        exit="exit"
                        initial="hidden"
                        animate="visible">
                        <ClipLoader color={"#84bd00"} css={'margin: 40px 10px;'} loading={loadingResults} size={40} /></motion.div>
                    :
                    <>
                        {error && <>
                            <Error msg={error} />
                            {Boolean(results && results.length) && <div className={styles.similarResults}>
                                <p>You might be interested in</p>
                            </div>}

                        </>}
                        <div className={styles.cardsWrapper}>
                            {/* Large Card */}
                            <motion.div animate={{ display: threeColLayout ? 'none' : 'grid' }}>
                                {results?.map(({ Url,
                                    Name,
                                    Abstract,
                                    Img,
                                    ReadTime,
                                    PublicationDate,
                                    ShowPublicationDateReadTimeContentType,
                                    ContentType,
                                    FeaturedText }, index) => (
                                    <div key={Name + index + 'resultWrapper'} style={{ width: tileWidth }}>
                                        <LargeCard
                                            tileWidth={tileWidth}
                                            key={'LargeCard' + index}
                                            id={'LargeCard' + index}
                                            Name={Name}
                                            Url={Url}
                                            index={index}
                                            variant={variant}
                                            isDesktop={isDesktop}
                                            Img={Img}
                                            Abstract={Abstract}
                                            ReadTime={ReadTime}
                                            PublicationDate={PublicationDate}
                                            ShowPublicationDateReadTimeContentType={ShowPublicationDateReadTimeContentType && _ShowPublicationDateReadTimeContentType}
                                            ContentType={ContentType}
                                            FeaturedText={FeaturedText}
                                        />
                                    </div>
                                )
                                )}
                            </motion.div>

                            {/* Related Content Cards */}
                            <motion.div
                                initial={{
                                    display: threeColLayout ? 'grid' : 'none',
                                    gap: '50px 40px',
                                    gridTemplateColumns: showFilters ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'
                                }}>
                                {results?.map(({ Url,
                                    Name,
                                    Abstract,
                                    Img,
                                    ReadTime,
                                    PublicationDate,
                                    ShowPublicationDateReadTimeContentType,
                                    ContentType,
                                    FeaturedText }, index) => (
                                    <div key={Name + index + 'resultWrapper'}>
                                        <RelatedContentCard
                                            key={'RelatedContentCard' + index}
                                            id={'RelatedContentCard' + index}
                                            Url={Url}
                                            HeroSectionImage1={null}
                                            className="related-content-card"
                                            LargeBlockImage={Img}
                                            index={index}
                                            Abstracts={true}
                                            Abstract={Abstract}
                                            isDesktop={isDesktop}
                                            Title={Name}
                                            ReadTime={ReadTime}
                                            PublicationDate={PublicationDate}
                                            ShowPublicationDateReadTimeContentType={ShowPublicationDateReadTimeContentType && _ShowPublicationDateReadTimeContentType}
                                            ContentType={ContentType}
                                            FeaturedText={FeaturedText}
                                        />
                                    </div>
                                )
                                )}
                            </motion.div>


                            <div style={{ width: '100%' }}>
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>

                                    {!error && filterResults.length > 1 && !similarResultsDisplayed &&
                                        <Button
                                            onClick={() => handleLoadResults()}
                                            buttonStyle="g-full"
                                            as="button">
                                            LOAD MORE
                                            {loadingMore && <ClipLoader color={"#ffffff"} css={'margin: 0 10px;'} loading={loadingMore} size={20} />}
                                        </Button>
                                    }
                                    {!error && !similarResultsDisplayed && filterResults.length === 1 &&
                                        <Button
                                            onClick={() => showSimilarResults()}
                                            buttonStyle="g-full"
                                            as="button">
                                            BROWSE SIMILAR
                                        </Button>
                                    }

                                </div>

                                {loadMoreError && <p className={styles.loadMoreError}>{loadMoreError}</p>}

                            </div>
                        </div>
                    </>
                }
            </AnimatePresence>
        </>
    )
};

const FilterResults = React.memo(_FilterResults);

export { FilterResults, }